import { useState, useEffect, useCallback } from 'react';
import { adfToHtml, isAdf } from '@/services/adf-to-html';
import { createConfluenceService } from '@/services/confluence-service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBookOpen,
  faCalendarDays,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
  faCodeCompare,
  faFileLines,
  faGlobe,
  faHashtag,
  faLink,
  faPenToSquare,
  faSpinner,
  faUser
} from '@fortawesome/free-solid-svg-icons';

interface ConfluencePageDetailProps {
  pageId: string;
  spaceKey: string;
  version?: number;
  onClose: () => void;
  onNavigate?: (pageId: string, spaceKey: string) => void;
}

interface ConfluencePage {
  id: string;
  title: string;
  version: { number: number; author?: any; when: string };
  author?: any;
  createdAt: string;
  modifiedAt: string;
  status: string;
  spaceKey: string;
  spaceName: string;
  url: string;
  content?: { id: string; type: string; representation: string; value: string };
  labels?: Array<{ id: string; name: string; prefix: string }>;
  childPages?: Array<{ id: string; title: string; status: string }>;
  attachments?: Array<{ id: string; filename: string; mimeType: string; size: number; downloadUrl: string }>;
}

function renderContent(content: { type: string; value: string } | undefined): string {
  if (!content?.value) return '';
  if (content.type === 'atlas_doc_format') {
    try { return adfToHtml(JSON.parse(content.value)); } catch { return content.value; }
  }
  try {
    const parsed = JSON.parse(content.value);
    if (isAdf(parsed)) return adfToHtml(parsed);
  } catch { /* not JSON */ }
  return content.value;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
}

type DiffLine = { type: 'added' | 'removed' | 'unchanged'; text: string };

function stripHtml(html: string): string {
  // Insert newlines at block-level boundaries before stripping
  const withBreaks = html
    .replace(/<\/?(p|div|li|tr|h[1-6]|blockquote|pre|br)[^>]*>/gi, '\n')
    .replace(/<\/?(td|th)[^>]*>/gi, ' | ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return withBreaks;
}

function computeLineDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n').filter(l => l.trim());
  const newLines = newText.split('\n').filter(l => l.trim());
  const MAX = 400;
  const a = oldLines.slice(0, MAX);
  const b = newLines.slice(0, MAX);
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] + 1 : Math.max(dp[i-1][j], dp[i][j-1]);
  const result: DiffLine[] = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i-1] === b[j-1]) { result.unshift({ type: 'unchanged', text: a[i-1] }); i--; j--; }
    else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) { result.unshift({ type: 'added', text: b[j-1] }); j--; }
    else { result.unshift({ type: 'removed', text: a[i-1] }); i--; }
  }
  return result;
}

const PersonBadge = ({ name, avatarUrl, label }: { name?: string; avatarUrl?: string; label: string }) => {
  if (!name) return <span className="text-xs text-gray-400 dark:text-slate-500">Unknown</span>;
  return (
    <div className="flex items-center gap-1.5">
      {avatarUrl
        ? <img src={avatarUrl} alt="" className="h-5 w-5 rounded-full" />
        : <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-gray-400 dark:text-slate-500" />}
      <div>
        <div className="text-xs text-gray-700 dark:text-slate-300 font-medium leading-tight">{name}</div>
        <div className="text-[10px] text-gray-400 dark:text-slate-500 leading-tight">{label}</div>
      </div>
    </div>
  );
};

export function ConfluencePageDetail({ pageId, spaceKey, version, onClose, onNavigate }: ConfluencePageDetailProps) {
  const [page, setPage] = useState<ConfluencePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['content']));
  const [diffLines, setDiffLines] = useState<DiffLine[] | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const fetchDiff = useCallback(async (currentPage: ConfluencePage) => {
    if (!version || version <= 1) return;
    setDiffLoading(true);
    setDiffError(null);
    setDiffLines(null);
    try {
      const svc = await createConfluenceService();
      if (!svc) throw new Error('Not connected');
      const prevPage = await svc.getPageWithContent(pageId, version - 1);
      const prevHtml = renderContent(prevPage.content);
      const currHtml = renderContent(currentPage.content);
      const prevText = stripHtml(prevHtml);
      const currText = stripHtml(currHtml);
      setDiffLines(computeLineDiff(prevText, currText));
    } catch (e: any) {
      setDiffError(e.message || 'Failed to load diff');
    } finally {
      setDiffLoading(false);
    }
  }, [pageId, version]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setPage(null);
    setDiffLines(null); setDiffError(null);
    (async () => {
      try {
        const svc = await createConfluenceService();
        if (!svc) throw new Error('Not connected to Confluence');
        const pageData = await svc.getPageWithContent(pageId, version);
        if (!cancelled) {
          setPage(pageData);
          if (version && version > 1) fetchDiff(pageData);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to fetch page');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pageId, spaceKey, version]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const link = (e.target as HTMLElement).closest('a.confluence-page-link');
    if (link && onNavigate) e.preventDefault();
  };

  // ── Renders INLINE inside the event row — no fixed/fullscreen positioning ──
  return (
    <div className="bg-white/95 dark:bg-slate-800/60 border-t border-b border-blue-500/20 animate-in slide-in-from-top-1 duration-200">
      {/* Compact header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 dark:bg-slate-800/80">
        <div className="flex items-center gap-2 min-w-0">
          <FontAwesomeIcon icon={faBookOpen} className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
          {loading && (
            <span className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading…
            </span>
          )}
          {error && (
            <span className="text-xs text-red-500 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-3 w-3" /> {error}
            </span>
          )}
          {page && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{page.title}</span>
              <span className="text-xs text-gray-400 dark:text-slate-500 flex-shrink-0">{page.spaceName} · v{page.version?.number}</span>
              {page.status && (
                <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  page.status === 'current'
                    ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300'
                }`}>{page.status}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {page && (
            <a href={page.url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors" title="Open in Confluence">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
            </a>
          )}
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors" title="Close">
            <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
          </button>
        </div>
      </div>

      {/* Version indicator */}
      {version && page && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
              <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" />
              <span className="font-medium">Viewing historical version {page.version?.number}</span>
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span className="text-amber-600 dark:text-amber-400">
                {formatDate(page.version?.when || page.modifiedAt)}
              </span>
              <span className="text-amber-600 dark:text-amber-400">•</span>
              <span className="text-amber-600 dark:text-amber-400">
                Showing content as it existed at this version
              </span>
            </div>
            {version > 1 && diffLoading && (
              <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading diff…
              </span>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {!loading && !error && page && (
        <div className="px-4 py-3 space-y-3">
          {/* Metadata grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <div className="text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
                <FontAwesomeIcon icon={faGlobe} className="h-3 w-3" /> Space
              </div>
              <span className="text-xs text-gray-700 dark:text-slate-300">{page.spaceName} <span className="text-gray-400 dark:text-slate-500">({page.spaceKey})</span></span>
            </div>
            <div>
              <div className="text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
                <FontAwesomeIcon icon={faHashtag} className="h-3 w-3" /> Page ID
              </div>
              <span className="text-xs text-gray-700 dark:text-slate-300 font-mono">{page.id}</span>
            </div>
            <div>
              <div className="text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
                <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" /> Created
              </div>
              <PersonBadge name={page.author?.displayName} avatarUrl={page.author?.avatarUrl} label={formatDate(page.createdAt)} />
            </div>
            <div>
              <div className="text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-1">
                <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" /> Last Modified
              </div>
              <PersonBadge name={page.version?.author?.displayName} avatarUrl={page.version?.author?.avatarUrl} label={timeAgo(page.modifiedAt)} />
            </div>
          </div>

          {/* Labels */}
          {page.labels && page.labels.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {page.labels.slice(0, 6).map(label => (
                <span key={label.id} className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 rounded-full">
                  {label.prefix ? `${label.prefix}:` : ''}{label.name}
                </span>
              ))}
              {page.labels.length > 6 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400 rounded-full">
                  +{page.labels.length - 6}
                </span>
              )}
            </div>
          )}

          {/* Page Content accordion */}
          <div className="border border-gray-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
            <button onClick={() => toggleSection('content')}
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors text-left">
              <div className="flex items-center gap-1.5">
                <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Page Content</span>
              </div>
              {expandedSections.has('content')
                ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
            </button>
            {expandedSections.has('content') && (
              <div className="px-3 py-3 border-t border-gray-200 dark:border-slate-700/40">
                {page.content?.value ? (
                  <div
                    className="prose prose-xs max-w-none dark:prose-invert text-xs text-gray-700 dark:text-slate-300
                      [&_p]:mb-1.5 [&_a]:text-blue-600 dark:[&_a]:text-blue-400
                      [&_code]:text-[11px] [&_code]:bg-gray-100 dark:[&_code]:bg-slate-700 [&_code]:px-1 [&_code]:rounded
                      [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-[11px] [&_pre]:overflow-x-auto
                      [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs [&_ul]:pl-4 [&_ol]:pl-4 [&_li]:mb-0.5"
                    dangerouslySetInnerHTML={{ __html: renderContent(page.content) }}
                    onClick={handleContentClick}
                  />
                ) : (
                  <span className="text-xs text-gray-400 dark:text-slate-500 italic">No content available</span>
                )}
              </div>
            )}
          </div>

          {/* Diff accordion — always shown for version > 1 */}
          {version && version > 1 && (
            <div className="border border-gray-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
              <div onClick={() => toggleSection('diff')}
                role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && toggleSection('diff')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors cursor-pointer select-none">
                <div className="flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faCodeCompare} className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">
                    Changes v{(version ?? 1) - 1} → v{version}
                  </span>
                  {diffLines && (
                    <span className="text-[10px] text-gray-400 dark:text-slate-500">
                      (<span className="text-emerald-500">+{diffLines.filter(l => l.type === 'added').length}</span>
                      {' / '}
                      <span className="text-rose-500">-{diffLines.filter(l => l.type === 'removed').length}</span> lines)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {diffLines && (
                    <button
                      onClick={e => { e.stopPropagation(); setShowUnchanged(v => !v); }}
                      className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600"
                    >
                      {showUnchanged ? 'Hide unchanged' : 'Show all'}
                    </button>
                  )}
                  {expandedSections.has('diff')
                    ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                    : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
                </div>
              </div>
              {expandedSections.has('diff') && (
                <div className="border-t border-gray-200 dark:border-slate-700/40">
                  {diffLoading && (
                    <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-400 dark:text-slate-500">
                      <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Computing diff…
                    </div>
                  )}
                  {diffError && (
                    <div className="flex items-center gap-2 px-3 py-3 text-xs text-red-500">
                      <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5" /> {diffError}
                    </div>
                  )}
                  {diffLines && (
                    <div className="overflow-x-auto font-mono text-[11px] max-h-96 overflow-y-auto">
                      {diffLines
                        .filter(l => showUnchanged || l.type !== 'unchanged')
                        .map((line, idx) => (
                          <div
                            key={idx}
                            className={
                              line.type === 'added'
                                ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-3 py-0.5 whitespace-pre-wrap'
                                : line.type === 'removed'
                                ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 px-3 py-0.5 whitespace-pre-wrap'
                                : 'text-gray-400 dark:text-slate-600 px-3 py-0.5 whitespace-pre-wrap'
                            }
                          >
                            <span className="select-none mr-2 opacity-50">
                              {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                            </span>
                            {line.text}
                          </div>
                        ))}
                      {diffLines.every(l => l.type === 'unchanged') && (
                        <div className="px-3 py-4 text-xs text-gray-400 dark:text-slate-500 text-center italic">
                          No visible text changes between these versions
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attachments accordion */}
          {page.attachments && page.attachments.length > 0 && (
            <div className="border border-gray-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
              <button onClick={() => toggleSection('attachments')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors text-left">
                <div className="flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Attachments ({page.attachments.length})</span>
                </div>
                {expandedSections.has('attachments')
                  ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                  : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
              </button>
              {expandedSections.has('attachments') && (
                <div className="px-3 py-2 border-t border-gray-200 dark:border-slate-700/40 space-y-1">
                  {page.attachments.map(att => (
                    <a key={att.id} href={att.downloadUrl} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-between py-1 px-1 rounded hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
                      <div className="flex items-center gap-1.5">
                        <FontAwesomeIcon icon={faFileLines} className="h-3 w-3 text-gray-400 dark:text-slate-500" />
                        <span className="text-xs text-gray-700 dark:text-slate-300">{att.filename}</span>
                      </div>
                      <span className="text-[10px] text-gray-400 dark:text-slate-500">{(att.size / 1024).toFixed(1)} KB</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Child Pages accordion */}
          {page.childPages && page.childPages.length > 0 && (
            <div className="border border-gray-200 dark:border-slate-700/40 rounded-lg overflow-hidden">
              <button onClick={() => toggleSection('children')}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors text-left">
                <div className="flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faBookOpen} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                  <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Child Pages ({page.childPages.length})</span>
                </div>
                {expandedSections.has('children')
                  ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />
                  : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
              </button>
              {expandedSections.has('children') && (
                <div className="px-3 py-2 border-t border-gray-200 dark:border-slate-700/40 space-y-1">
                  {page.childPages.map(child => (
                    <div key={child.id} className="flex items-center justify-between py-1 px-1">
                      <div className="flex items-center gap-1.5">
                        <FontAwesomeIcon icon={faBookOpen} className="h-3 w-3 text-gray-400 dark:text-slate-500" />
                        <span className="text-xs text-gray-700 dark:text-slate-300">{child.title}</span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        child.status === 'current'
                          ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>{child.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
